import React, {useCallback, useEffect, useRef, useState} from "react";
import Cookies from "js-cookie";
import {jwtDecode} from "jwt-decode";
import {AUTH_TOKEN} from "../constants/Constants";
import {
    getRefreshToken,
    logout,
    setAuthTokenCookie,
    setRefreshTokenCookie,
} from "../utils/Utils";
import AxiosAgent from "../api/AxiosAgent";
import api from "../api/api";

/** How many seconds before expiry to attempt a silent background refresh */
const SILENT_REFRESH_THRESHOLD_SECS = 120; // 2 minutes
/** Only show the warning modal if token has THIS many seconds or fewer left after silent refresh fails */
const WARNING_BEFORE_EXPIRY_SECS = 30; // 30 seconds — last resort
/** How often to check the token (ms) */
const CHECK_INTERVAL_MS = 30_000; // every 30 seconds
/** sessionStorage key used to persist the last-known token expiry */
const EXPIRY_STORAGE_KEY = "blift-session-expiry";

/**
 * Public routes where the session warning should never appear.
 * These are pages accessible without a valid session.
 */
const PUBLIC_PATHS = [
    "/",
    "/rcic/registration",
    "/client/registration",
    "/rcic/registration-second-step",
    "/client/registration-second-step",
    "/forgot-password",
    "/reset-password",
    "/verify-email",
    "/rcic/reset-pin",
    "/unauthorized-access",
    "/support",
    "/rcic-search",

];

/** Returns true if the current page is a public (unauthenticated) route. */
const isPublicRoute = (): boolean => {
    const path = window.location.pathname;
    // Exact match or starts-with for parameterised public routes
    return PUBLIC_PATHS.some((p) => path === p || path.startsWith("/verify-email"));
};

const SessionExpiryWarning: React.FC = () => {
    const [visible, setVisible] = useState(false);
    const [expired, setExpired] = useState(false);
    const [countdown, setCountdown] = useState(WARNING_BEFORE_EXPIRY_SECS);

    const visibleRef = useRef(false);
    const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const checkRef = useRef<ReturnType<typeof setInterval> | null>(null);

    /**
     * Read the token expiry (epoch seconds).
     * Primary: decode the JWT from the auth-token cookie.
     * Fallback: read from sessionStorage (for when browser deletes the cookie near expiry).
     * Always persists the expiry back to sessionStorage when a valid cookie is found.
     */
    const getTokenExpiry = (): number | null => {
        // Never show the warning on public/login pages
        if (isPublicRoute()) return null;

        const token = Cookies.get(AUTH_TOKEN);
        if (token && token !== "undefined") {
            try {
                const decoded: any = jwtDecode(token);
                const exp = decoded.exp ?? null;
                if (exp) {
                    sessionStorage.setItem(EXPIRY_STORAGE_KEY, String(exp));
                }
                return exp;
            } catch {
                // JWT decode failed — fall through to sessionStorage
            }
        }

        // Cookie is gone — check if we previously saved an expiry
        const saved = sessionStorage.getItem(EXPIRY_STORAGE_KEY);
        if (saved) {
            const exp = parseInt(saved, 10);
            if (!isNaN(exp)) return exp;
        }

        return null;
    };

    const stopCountdown = () => {
        if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
        }
    };

    const showWarning = (secondsLeft: number) => {
        if (visibleRef.current) return;
        visibleRef.current = true;
        setCountdown(Math.max(0, Math.floor(secondsLeft)));
        setExpired(false);
        setVisible(true);

        countdownRef.current = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    stopCountdown();
                    setExpired(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const showExpired = () => {
        if (visibleRef.current) return;
        visibleRef.current = true;
        setExpired(true);
        setCountdown(0);
        setVisible(true);
    };

    const hideModal = useCallback(() => {
        stopCountdown();
        visibleRef.current = false;
        setVisible(false);
        setExpired(false);
    }, []);

    /** Full logout — clear session data and redirect to login. */
    const handleLogout = useCallback(async () => {
        hideModal();
        sessionStorage.removeItem(EXPIRY_STORAGE_KEY);
        try {
            await logout();
        } catch {
            // best-effort
        }
        window.location.href = "/";
    }, [hideModal]);

    /**
     * Refresh the access token.
     * hideModal() is only called AFTER the refresh succeeds so the user
     * is never left in a broken state if the refresh fails.
     */
    const handleStayLoggedIn = useCallback(async () => {
        try {
            const refreshToken = getRefreshToken();
            if (!refreshToken) {
                await handleLogout();
                return;
            }

            const rs = await AxiosAgent.requests.postNoToken(
                api.USER + "/token-by-refresh-token",
                {stringParam: refreshToken}
            );

            // Detect failure: errorHandling sets data.type="serverError"; also
            // check for explicit error status codes or missing token in response.
            const isError =
                rs.data?.type === "serverError" ||
                rs.response?.status === 401 ||
                rs.response?.status === 500 ||
                (!rs.data?.access_token && !rs.data?.accessToken);

            if (isError) {
                await handleLogout();
                return;
            }

            // Support both camelCase and snake_case token field names
            const newAccessToken = rs.data.access_token ?? rs.data.accessToken;
            const newRefreshToken = rs.data.refresh_token ?? rs.data.refreshToken;

            await setAuthTokenCookie(newAccessToken);
            await setRefreshTokenCookie(newRefreshToken);

            // Persist the new expiry immediately
            try {
                const decoded: any = jwtDecode(newAccessToken);
                if (decoded?.exp) {
                    sessionStorage.setItem(EXPIRY_STORAGE_KEY, String(decoded.exp));
                }
            } catch {
                // non-critical
            }

            // Only dismiss the modal after everything succeeded
            hideModal();
        } catch {
            await handleLogout();
        }
    }, [hideModal, handleLogout]);

    /**
     * Silently refresh the access token without showing any UI.
     * Returns true if refresh succeeded, false otherwise.
     */
    const silentRefresh = useCallback(async (): Promise<boolean> => {
        try {
            const refreshToken = getRefreshToken();
            if (!refreshToken) return false;

            const rs = await AxiosAgent.requests.postNoToken(
                api.USER + "/token-by-refresh-token",
                {stringParam: refreshToken}
            );

            const isError =
                rs.data?.type === "serverError" ||
                rs.response?.status === 401 ||
                rs.response?.status === 500 ||
                (!rs.data?.access_token && !rs.data?.accessToken);

            if (isError) return false;

            const newAccessToken = rs.data.access_token ?? rs.data.accessToken;
            const newRefreshToken = rs.data.refresh_token ?? rs.data.refreshToken;

            await setAuthTokenCookie(newAccessToken);
            await setRefreshTokenCookie(newRefreshToken);

            try {
                const decoded: any = jwtDecode(newAccessToken);
                if (decoded?.exp) {
                    sessionStorage.setItem(EXPIRY_STORAGE_KEY, String(decoded.exp));
                }
            } catch { /* non-critical */ }

            return true;
        } catch {
            return false;
        }
    }, []);

    /** Single stable effect: set up the periodic token check on mount only. */
    useEffect(() => {
        let refreshInProgress = false;

        const check = () => {
            const exp = getTokenExpiry();
            if (exp === null) return;

            const nowSecs = Math.floor(Date.now() / 1000);
            const remaining = exp - nowSecs;

            if (remaining <= 0) {
                // Token already expired — attempt silent refresh, logout if fails
                if (refreshInProgress) return;
                refreshInProgress = true;
                silentRefresh().then((ok) => {
                    refreshInProgress = false;
                    if (!ok) showExpired();
                });
            } else if (remaining <= SILENT_REFRESH_THRESHOLD_SECS) {
                // Approaching expiry — silently refresh in background
                // Only interrupt the user (show modal) if refresh fails AND < WARNING_BEFORE_EXPIRY_SECS remain
                if (refreshInProgress) return;
                refreshInProgress = true;
                silentRefresh().then((ok) => {
                    refreshInProgress = false;
                    if (!ok) {
                        // Re-read remaining time after failed refresh attempt
                        const newExp = getTokenExpiry();
                        const newRemaining = newExp ? newExp - Math.floor(Date.now() / 1000) : 0;
                        if (newRemaining <= WARNING_BEFORE_EXPIRY_SECS) {
                            showWarning(Math.max(0, newRemaining));
                        }
                    }
                });
            }
        };

        check(); // initial mount
        checkRef.current = setInterval(check, CHECK_INTERVAL_MS);

        return () => {
            if (checkRef.current) clearInterval(checkRef.current);
            stopCountdown();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!visible) return null;

    const minutes = Math.floor(countdown / 60);
    const seconds = countdown % 60;
    const timeDisplay =
        minutes > 0
            ? `${minutes}:${seconds.toString().padStart(2, "0")}`
            : `${seconds}s`;

    return (
        <div className="custom-toast-wrapper">
            <div className="custom-toast-content">
                <div className="custom-toast-header">
                    <div className="custom-toast-title">
                        {expired ? "Session Expired" : "Session Expiring"}
                    </div>
                </div>
                <div className="custom-toast-body" style={{flexDirection: "column", alignItems: "center", textAlign: "center"}}>
                    {expired ? (
                        <p className="text-lead" style={{margin: "1rem 0"}}>
                            Your current session has expired. Please log in again to continue.
                        </p>
                    ) : (
                        <>
                            <p style={{fontSize: 40, fontWeight: 700, color: "#dc3545", margin: "0.5rem 0"}}>
                                {timeDisplay}
                            </p>
                            <p className="text-lead">
                                Your session will expire soon. Do you want to stay logged in?
                            </p>
                        </>
                    )}
                </div>
                <div className="custom-toast-footer" style={{justifyContent: "right"}}>
                    <button className="btn btn-cancel" onClick={handleLogout}>
                        Logout
                    </button>
                    <button
                        className="btn btn-confirm"
                        onClick={handleStayLoggedIn}
                        disabled={expired}
                        style={expired ? {opacity: 0.5, cursor: "not-allowed"} : {}}
                    >
                        Stay Logged In
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SessionExpiryWarning;

